import { z } from "zod";

import { RechargeWebhookTopicList } from "../dtos/recharge-integrations";
import { castToString } from "../utils";

const RechargePropertySchema = z.object({
  name: z.string(),
  value: z.string().or(z.number()),
});

const RechargeAnalyticsSchema = z.object({
  utm_params: z.array(z.any()).default([]),
});

export const RechargeSubscriptionSchema = z.object({
  id: z.number(),
  address_id: z.number(),
  analytics_data: RechargeAnalyticsSchema,
  cancellation_reason: z.string().nullish(),
  cancellation_reason_comments: z.string().nullish(),
  cancelled_at: z.string().nullish(),
  charge_interval_frequency: z.string(),
  created_at: z.string(),
  customer_id: z.number(),
  email: z.string(),
  expire_after_specific_number_of_charges: castToString.nullish(),
  has_queued_charges: z.number(),
  is_prepaid: z.boolean(),
  is_skippable: z.boolean(),
  is_swappable: z.boolean(),
  max_retries_reached: z.number(),
  next_charge_scheduled_at: z.string().nullish(),
  order_day_of_month: z.number().nullish(),
  order_day_of_week: z.number().nullish(),
  order_interval_frequency: z.string(),
  order_interval_unit: z.string(),
  presentment_currency: z.string(),
  price: z.number(),
  product_title: z.string(),
  properties: RechargePropertySchema.array().default([]),
  quantity: z.number(),
  recharge_product_id: z.number().nullish(),
  shopify_product_id: z.number(),
  shopify_variant_id: z.number(),
  sku: z.string().nullish(),
  sku_override: z.boolean(),
  status: z.string(),
  updated_at: z.string(),
  variant_title: z.string(),
});

export type RechargeSubscriptionType = z.infer<
  typeof RechargeSubscriptionSchema
>;

export const RechargeSubscriptionResponseSchema = z.object({
  subscription: RechargeSubscriptionSchema,
});

export type RechargeSubscriptionResponseType = z.infer<
  typeof RechargeSubscriptionResponseSchema
>;

const RechargeAddressSchema = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  company: z.string().nullish(),
  country: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  phone: z.string().nullish(),
  province: z.string().nullish(),
  zip: z.string().nullish(),
});

const RechargeImageSchema = z.object({
  large: z.string(),
  medium: z.string(),
  original: z.string(),
  small: z.string(),
});

const RechargeLineItemSchema = z.object({
  images: RechargeImageSchema,
  price: z.number(),
  properties: RechargePropertySchema.array().default([]),
  quantity: z.number(),
  shopify_product_id: z.number(),
  shopify_variant_id: z.number(),
  sku: z.string(),
  subscription_id: z.number(),
  variant_title: z.string(),
});

const RechargeShippingLineSchema = z.object({
  code: z.string(),
  price: z.number(),
  title: z.string(),
});

const RechargeTaxLineSchema = z.object({
  price: z.number(),
  rate: z.number(),
  title: z.string(),
});

const RechargeCustomerSchema = z.object({
  accepts_marketing: z.boolean().nullish(),
  email: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  phone: z.string().nullish(),
  send_email_welcome: z.boolean(),
  verified_email: z.boolean(),
});

const RechargeOrderSchema = z.object({
  id: z.number(),
  address_id: z.number(),
  address_is_active: z.number(),
  billing_address: RechargeAddressSchema,
  charge_id: z.number(),
  charge_status: z.string(),
  created_at: z.string(),
  currency: z.string(),
  customer: RechargeCustomerSchema,
  customer_id: z.number(),
  discount_codes: z.array(z.any()),
  email: z.string(),
  first_name: z.string(),
  hash: z.string(),
  is_prepaid: z.number(),
  last_name: z.string(),
  line_items: RechargeLineItemSchema.array(),
  note: z.string(),
  note_attributes: z.array(z.any()),
  payment_processor: z.string(),
  processed_at: z.string().nullish(),
  scheduled_at: z.string(),
  shipped_date: z.string().nullish(),
  shipping_address: RechargeAddressSchema,
  shipping_date: z.string(),
  shipping_lines: RechargeShippingLineSchema.array().default([]),
  shopify_cart_token: z.string(),
  shopify_customer_id: z.string(),
  shopify_id: z.string().nullish(),
  shopify_order_id: z.string().nullish(),
  shopify_order_number: z.string().nullish(),
  status: z.string(),
  subtotal_price: z.number(),
  tags: z.string(),
  tax_lines: RechargeTaxLineSchema.array().default([]),
  total_discounts: z.number(),
  total_line_items_price: z.number(),
  total_price: z.number(),
  total_refunds: z.string().nullish(),
  total_tax: z.number(),
  total_weight: z.number(),
  transaction_id: z.string(),
  type: z.string(),
  updated_at: z.string(),
});
export type RechargeOrderType = z.infer<typeof RechargeOrderSchema>;

export const RechargeWebhookSchema = z.object({
  id: z.string(),
  address: z.string(),
  included_objects: z.array(z.string()).default([]),
  topic: z.enum(RechargeWebhookTopicList),
  version: z.string(),
});
export type RechargeWebhookType = z.infer<typeof RechargeWebhookSchema>;

export const RechargeWebhookResponseSchema = z.object({
  webhook: RechargeWebhookSchema,
});

export type RechargeWebhookResponseType = z.infer<
  typeof RechargeWebhookResponseSchema
>;

export const RechargeSubscriptionDefaults = z.object({
  charge_interval_frequency: z.number().nullish(),
  cutoff_day_of_month: z.number().nullish(),
  cutoff_day_of_week: z.number().nullish(),
  expire_after_specific_number_of_charges: z.number().nullish(),
  modifiable_properties: z.string().array(),
  number_charges_until_expiration: z.number().nullish(),
  order_day_of_month: z.number().nullish(),
  order_day_of_week: z.number().nullish(),
  order_interval_frequency_options: z.string().array(),
  order_interval_unit: z.string().nullish(),
  storefront_purchase_options: z.string().nullish(),
});

export const RechargeProductSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  discount_amount: z.number(),
  discount_type: z.string(),
  handle: z.string().nullish(),
  images: RechargeImageSchema,
  product_id: z.number(),
  shopify_product_id: z.number(),
  subscription_defaults: RechargeSubscriptionDefaults,
  title: z.string(),
  updated_at: z.string(),
});

export type RechargeProductType = z.infer<typeof RechargeProductSchema>;
